import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import DuruScan from "@/pages/dashboard/DuruScan";
import Studio from "@/pages/dashboard/Studio";
import ConsentBanner from "@/components/ConsentBanner";
import { CircleNotch } from "@phosphor-icons/react";

// Eager: public entry + auth (small, first-paint critical)
import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";

// Lazy: everything secondary / authenticated (kept out of the homepage bundle)
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const Legal = lazy(() => import("@/pages/Legal"));
const SharedDocument = lazy(() => import("@/pages/SharedDocument"));
const ProtectedView = lazy(() => import("@/pages/ProtectedView"));
const Verify = lazy(() => import("@/pages/Verify"));
const VerifyRecord = lazy(() => import("@/pages/VerifyRecord"));
const Countries = lazy(() => import("@/pages/Countries"));
const IssuerApply = lazy(() => import("@/pages/IssuerApply"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Sign = lazy(() => import("@/pages/Sign"));
const RoomGuest = lazy(() => import("@/pages/RoomGuest"));
const SecureAccount = lazy(() => import("@/pages/SecureAccount"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const Home = lazy(() => import("@/pages/dashboard/Home"));
const Vault = lazy(() => import("@/pages/dashboard/Vault"));
const Shares = lazy(() => import("@/pages/dashboard/Shares"));
const Plans = lazy(() => import("@/pages/dashboard/Plans"));
const Settings = lazy(() => import("@/pages/dashboard/Settings"));
const Verifications = lazy(() => import("@/pages/dashboard/Verifications"));
const Signatures = lazy(() => import("@/pages/dashboard/Signatures"));
const Shield = lazy(() => import("@/pages/dashboard/Shield"));
const Kiosk = lazy(() => import("@/pages/dashboard/Kiosk"));
const Assistant = lazy(() => import("@/pages/dashboard/Assistant"));
const Admin = lazy(() => import("@/pages/dashboard/Admin"));
const Checks = lazy(() => import("@/pages/dashboard/Checks"));
const VerifyCenter = lazy(() => import("@/pages/dashboard/VerifyCenter"));
const VerifyCertificate = lazy(() => import("@/pages/VerifyCertificate"));
const VerificationComplete = lazy(() => import("@/pages/VerificationComplete"));
const Organizations = lazy(() => import("@/pages/dashboard/Organizations"));
const CommandCenter = lazy(() => import("@/pages/dashboard/CommandCenter"));
const Rooms = lazy(() => import("@/pages/dashboard/Rooms"));
const RoomDetail = lazy(() => import("@/pages/dashboard/RoomDetail"));
const Flow = lazy(() => import("@/pages/dashboard/Flow"));
const FlowDetail = lazy(() => import("@/pages/dashboard/FlowDetail"));
const DocumentTwin = lazy(() => import("@/pages/dashboard/DocumentTwin"));
const Pulse = lazy(() => import("@/pages/dashboard/Pulse"));
const NotFound = lazy(() => import("@/pages/NotFound"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="route-loader">
      <CircleNotch size={34} className="animate-spin text-primary" />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Canonical-host guard: normalize www → apex on the production domain.
  // The authoritative HTTP→HTTPS and www→apex 301s must also be set at DNS/CDN for SEO.
  if (typeof window !== "undefined" && window.location.hostname === "www.duruvault.com") {
    window.location.replace("https://duruvault.com" + window.location.pathname + window.location.search + window.location.hash);
    return null;
  }
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/legal/:page" element={<Legal />} />
        <Route path="/s/:token" element={<SharedDocument />} />
        <Route path="/protected-view/:token" element={<ProtectedView />} />
        <Route path="/verify/:token" element={<Verify />} />
        <Route path="/v/:token" element={<VerifyRecord />} />
        <Route path="/vc/:token" element={<VerifyCertificate />} />
        <Route path="/countries" element={<Countries />} />
        <Route path="/countries/:slug" element={<Countries />} />
        <Route path="/for-institutions" element={<IssuerApply />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/sign/:token" element={<Sign />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/secure/:token" element={<SecureAccount />} />
        <Route path="/verification/complete" element={<ProtectedRoute><VerificationComplete /></ProtectedRoute>} />
        <Route path="/room-guest/:token" element={<RoomGuest />} />
        <Route path="/dashboard/kiosk/:id" element={<ProtectedRoute><Kiosk /></ProtectedRoute>} />        <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          <Route index element={<Home />} />
          <Route path="vault" element={<Vault />} />
          <Route path="scan" element={<DuruScan />} />
          <Route path="studio" element={<Studio />} />
          <Route path="shares" element={<Shares />} />
          <Route path="verify" element={<Verifications />} />
          <Route path="checks" element={<Checks />} />
          <Route path="verify-center" element={<VerifyCenter />} />
          <Route path="my-verified-documents" element={<VerifyCenter documentsOnly />} />
          <Route path="organizations" element={<Organizations />} />
          <Route path="command" element={<CommandCenter />} />
          <Route path="rooms" element={<Rooms />} />
          <Route path="rooms/:roomId" element={<RoomDetail />} />
          <Route path="flow" element={<Flow />} />
          <Route path="flow/:flowId" element={<FlowDetail />} />
          <Route path="twin/:docId" element={<DocumentTwin />} />
          <Route path="pulse" element={<Pulse />} />
          <Route path="sign" element={<Signatures />} />
          <Route path="shield" element={<Shield />} />
          <Route path="assistant" element={<Assistant />} />
          <Route path="plans" element={<Plans />} />
          <Route path="settings" element={<Settings />} />
          <Route path="admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <a
            href="#main-content"
            data-testid="skip-to-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-primary focus:text-[#20242a] focus:font-semibold focus:shadow-lg focus:outline-none"
          >
            Skip to content
          </a>
          <AppRoutes />
          <ConsentBanner />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
